/**
 * O que deu errado na tela, em texto — o que o print não entrega.
 *
 *   window.onerror ─────┐
 *   unhandledrejection ─┼─▶ buffer circular (N últimos) ─▶ pageContext no anexo da captura
 *   fetch / XHR ────────┘
 *
 * RISCO CENTRAL, e a razão do cuidado obsessivo abaixo: para saber que a chamada de salvar voltou
 * 500, é preciso embrulhar o `fetch` e o `XMLHttpRequest` DA PÁGINA DO CLIENTE. Um erro aqui não
 * quebra o Copilot — quebra o sistema dele. Por isso:
 *
 *   1. o wrapper SEMPRE devolve o resultado original, sem tocar nele;
 *   2. todo registro roda dentro de try/catch e desiste em silêncio se falhar;
 *   3. rejeição de rede continua rejeitando, com o erro original, na mesma ordem;
 *   4. instalar duas vezes não empilha wrappers.
 */

const MAX_CONSOLE_ERRORS = 5;
const MAX_MESSAGE_CHARS = 300;
const MAX_BODY_CHARS = 500;

export interface FailedRequestInfo {
  method: string;
  url: string;
  status: number;
  body?: string;
}

export interface PageErrorContext {
  consoleErrors: string[];
  failedRequest?: FailedRequestInfo;
}

const consoleErrors: string[] = [];
let failedRequest: FailedRequestInfo | undefined;
let installed = false;

function clip(value: unknown, max: number): string {
  return String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);
}

function pushConsoleError(message: string): void {
  try {
    const text = clip(message, MAX_MESSAGE_CHARS);
    if (!text) return;
    consoleErrors.push(text);
    while (consoleErrors.length > MAX_CONSOLE_ERRORS) consoleErrors.shift();
  } catch {
    /* registrar nunca pode atrapalhar a página */
  }
}

function recordFailedRequest(info: FailedRequestInfo): void {
  try {
    failedRequest = {
      method: clip(info.method, 10).toUpperCase(),
      url: clip(info.url, MAX_MESSAGE_CHARS),
      status: info.status,
      ...(info.body ? { body: clip(info.body, MAX_BODY_CHARS) } : {})
    };
  } catch {
    /* idem */
  }
}

export function installErrorBuffer(target: Window & typeof globalThis = window): void {
  if (installed) return;
  installed = true;

  target.addEventListener('error', event => {
    pushConsoleError((event as ErrorEvent).message || 'erro de script');
  });

  target.addEventListener('unhandledrejection', event => {
    const reason = (event as PromiseRejectionEvent).reason;
    pushConsoleError(reason?.message || reason || 'promessa rejeitada');
  });

  const originalFetch = target.fetch;
  if (typeof originalFetch === 'function') {
    target.fetch = function patchedFetch(this: unknown, ...args: unknown[]) {
      const result = (originalFetch as (...a: unknown[]) => Promise<Response>).apply(
        this ?? target,
        args
      );
      try {
        return result.then(
          response => {
            try {
              if (!response.ok) {
                const request = args[0] as Request | string;
                const url = typeof request === 'string' ? request : (request as Request)?.url || '';
                const method =
                  ((args[1] as RequestInit)?.method as string) ||
                  (request as Request)?.method ||
                  'GET';
                recordFailedRequest({ method, url, status: response.status });
                response
                  .clone()
                  .text()
                  .then(body => {
                    if (failedRequest && failedRequest.url === clip(url, MAX_MESSAGE_CHARS)) {
                      failedRequest.body = clip(body, MAX_BODY_CHARS);
                    }
                  })
                  .catch(() => undefined);
              }
            } catch {
              /* nunca alterar o que a página recebe */
            }
            return response;
          },
          error => {
            try {
              const request = args[0] as Request | string;
              const url = typeof request === 'string' ? request : (request as Request)?.url || '';
              recordFailedRequest({ method: 'GET', url, status: 0 });
            } catch {
              /* idem */
            }
            throw error;
          }
        );
      } catch {
        return result;
      }
    } as typeof target.fetch;
  }

  const OriginalXhrSend = target.XMLHttpRequest?.prototype?.send;
  const OriginalXhrOpen = target.XMLHttpRequest?.prototype?.open;
  if (typeof OriginalXhrSend === 'function' && typeof OriginalXhrOpen === 'function') {
    target.XMLHttpRequest.prototype.open = function patchedOpen(
      this: XMLHttpRequest & { __gdMethod?: string; __gdUrl?: string },
      method: string,
      url: string,
      ...rest: unknown[]
    ) {
      try {
        this.__gdMethod = method;
        this.__gdUrl = url;
      } catch {
        /* idem */
      }
      return (OriginalXhrOpen as (...a: unknown[]) => void).apply(this, [method, url, ...rest]);
    } as typeof target.XMLHttpRequest.prototype.open;

    target.XMLHttpRequest.prototype.send = function patchedSend(
      this: XMLHttpRequest & { __gdMethod?: string; __gdUrl?: string },
      ...args: unknown[]
    ) {
      try {
        this.addEventListener('loadend', () => {
          try {
            if (this.status >= 400 || this.status === 0) {
              recordFailedRequest({
                method: this.__gdMethod || 'GET',
                url: this.__gdUrl || '',
                status: this.status,
                body: typeof this.responseText === 'string' ? this.responseText : undefined
              });
            }
          } catch {
            /* idem */
          }
        });
      } catch {
        /* idem */
      }
      return (OriginalXhrSend as (...a: unknown[]) => void).apply(this, args);
    } as typeof target.XMLHttpRequest.prototype.send;
  }
}

/** Texto de alerta visível na tela — o erro que o usuário está lendo agora. */
export function readVisibleAlerts(doc: Document = document): string[] {
  const selectors = ['[role="alert"]', '.alert-danger', '.error', '.is-invalid', '[data-error]'];
  const seen: string[] = [];
  for (const selector of selectors) {
    let nodes: NodeListOf<Element>;
    try {
      nodes = doc.querySelectorAll(selector);
    } catch {
      continue;
    }
    nodes.forEach(node => {
      const text = clip((node as HTMLElement).innerText || node.textContent, MAX_MESSAGE_CHARS);
      if (text && !seen.includes(text)) seen.push(text);
    });
    if (seen.length >= MAX_CONSOLE_ERRORS) break;
  }
  return seen.slice(0, MAX_CONSOLE_ERRORS);
}

export function readPageErrorContext(): PageErrorContext {
  return {
    consoleErrors: [...consoleErrors],
    ...(failedRequest ? { failedRequest: { ...failedRequest } } : {})
  };
}

export function __resetErrorBufferForTests(): void {
  consoleErrors.length = 0;
  failedRequest = undefined;
  installed = false;
}
